import React, { useState } from 'react'
import { LocationPicker } from './LocationPicker'

interface LocationPickerWithModalProps {
  latitude: string
  longitude: string
  locationName: string
  onLocationChange: (lat: number, lng: number, name: string) => void
  onLocationNameChange: (name: string) => void
  disabled?: boolean
  hasError?: boolean
  hideNameInput?: boolean
}

export const LocationPickerWithModal: React.FC<LocationPickerWithModalProps> = ({
  latitude,
  longitude,
  locationName,
  onLocationChange,
  onLocationNameChange,
  disabled = false,
  hasError = false,
  hideNameInput = false
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [tempLat, setTempLat] = useState(latitude)
  const [tempLng, setTempLng] = useState(longitude)
  const [tempName, setTempName] = useState(locationName)

  const hasLocation = latitude && longitude

  const handleOpenModal = () => {
    setTempLat(latitude)
    setTempLng(longitude)
    setTempName(locationName)
    setIsModalOpen(true)
  }

  const handleSave = () => {
    if (tempLat && tempLng) {
      onLocationChange(parseFloat(tempLat), parseFloat(tempLng), tempName)
      setIsModalOpen(false)
    }
  }

  const handleCancel = () => {
    setTempLat(latitude)
    setTempLng(longitude)
    setTempName(locationName)
    setIsModalOpen(false)
  }

  const handleTempLocationSelect = (lat: number, lng: number, name: string) => {
    setTempLat(lat.toString())
    setTempLng(lng.toString())
    setTempName(name)
  }

  const thumbnailBorder = hasError
    ? 'border-2 border-dashed border-red-500'
    : hasLocation
      ? 'border border-[#d9d9d9]'
      : 'border-2 border-dashed border-[#d9d9d9]'

  return (
    <div>
      {/* Location Name Input */}
      {!hideNameInput && (
        <div className="mb-4">
          <label htmlFor="locationName" className="block mb-2 text-sm font-medium text-black/85">
            Название локации <span className="text-red-500">*</span>
          </label>
          <input
            id="locationName"
            type="text"
            value={locationName}
            onChange={(e) => onLocationNameChange(e.target.value)}
            placeholder="Например: Парк Ата-Тюрк, Бишкек"
            disabled={disabled}
            className={`w-full px-3 py-2 text-sm rounded-md outline-none transition-colors text-black/85 focus:border-[#1890ff] border ${
              hasError ? 'border-red-500' : 'border-[#d9d9d9]'
            }`}
          />
        </div>
      )}

      {/* Mini Map Thumbnail / Button */}
      <div
        onClick={disabled ? undefined : handleOpenModal}
        className={`relative w-full h-[200px] rounded-lg overflow-hidden transition-all flex items-center justify-center ${thumbnailBorder} ${
          hasLocation ? 'bg-white' : 'bg-[#fafafa]'
        } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${
          !disabled && hasLocation && !hasError ? 'hover:border-[#1890ff] hover:shadow-[0_2px_8px_rgba(24,144,255,0.15)]' : ''
        }`}
      >
        {hasLocation ? (
          <iframe
            src={`https://maps.google.com/maps?q=${latitude},${longitude}&z=13&output=embed&gestureHandling=none`}
            className="w-full h-full border-0 pointer-events-none"
            title="Location preview"
          />
        ) : (
          <div className={`text-center p-5 ${disabled ? 'text-black/25' : 'text-black/45'}`}>
            <div className="text-5xl mb-3 opacity-50">🗺️</div>
            <div className={`text-sm font-medium mb-1 ${disabled ? 'text-black/25' : 'text-[#1890ff]'}`}>
              {disabled ? 'Локация не указана' : 'Указать на карте'}
            </div>
            {!disabled && (
              <div className="text-xs text-black/45">
                Нажмите для выбора на карте
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/65 flex items-center justify-center p-5 z-[10000]"
          onClick={handleCancel}
        >
          <div
            className="bg-white rounded-xl w-full max-w-[900px] max-h-[90vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold m-0 text-black/85">
                Выберите локацию на карте
              </h3>
              <button
                onClick={handleCancel}
                className="w-8 h-8 rounded-full border-0 bg-[#fafafa] text-black/65 cursor-pointer flex items-center justify-center text-xl transition-colors hover:bg-[#f0f0f0]"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 flex-1 overflow-auto">
              <LocationPicker
                latitude={tempLat}
                longitude={tempLng}
                locationName={tempName}
                onLocationSelect={handleTempLocationSelect}
                disabled={false}
              />
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
              <button
                onClick={handleCancel}
                className="px-5 py-2 text-sm font-medium border border-[#d9d9d9] rounded-md bg-white text-black/85 cursor-pointer transition-colors hover:border-[#1890ff] hover:text-[#1890ff]"
              >
                Отменить
              </button>
              <button
                onClick={handleSave}
                disabled={!tempLat || !tempLng}
                className={`px-5 py-2 text-sm font-medium border-0 rounded-md text-white transition-colors ${
                  !tempLat || !tempLng
                    ? 'bg-[#d9d9d9] cursor-not-allowed'
                    : 'bg-[#1890ff] cursor-pointer hover:bg-[#40a9ff]'
                }`}
              >
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
